import React, { memo, useEffect, useState } from 'react';
// We have deprecated emotion. Please use compiled instead
// eslint-disable-next-line no-restricted-imports, @atlaskit/ui-styling-standard/use-compiled -- Ignored via go/DSP-18766
import styled from '@emotion/styled';
import { css } from '@compiled/react';
import { defineMessages, useIntl } from 'react-intl-next';
import type { Dispatch } from 'react';

import { token } from '@atlaskit/tokens';

import { fg } from '@confluence/feature-gating';

import { TaskView } from '../tasksDefinitions';
import type { TaskPages, TaskSpaces } from '../tasksDefinitions';

import { SelectDueDate } from './SelectDueDate';
import { SelectCompletion } from './SelectCompletion';
import { SelectCreator } from './SelectCreator';
import { SelectAssignee } from './SelectAssignee';
import { SelectPage } from './SelectPage';
import { SelectSpace } from './SelectSpace';
import {
	FilterHeader,
	InputContainer,
	SelectContainer,
	ClearButton,
	ClearContainer,
	FilterHeaderCompiled,
	InputContainerCompiled,
	SelectContainerCompiled,
	ClearButtonCompiled,
} from './presentationalComponents';
import type { FiltersState } from './useFiltersReducer';
import { FilterKeys } from './filtersDefinitions';

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const FiltersContainer = styled.div({
	padding: token('space.150', '12px'),
	width: 360,
});

const filtersContainerStyles = css({
	padding: token('space.150', '12px'),
	width: 360,
});

const clearContainerStyles = css({
	display: 'inline-block',
	width: '100%',
	textAlign: 'right',
});

const i18n = defineMessages({
	filterLabel: {
		id: 'home.views.tasks.filters.label.filterLabel',
		defaultMessage: 'FILTER',
		description: 'The header text for the filter label column that lists the filter names.',
	},
	clearFilters: {
		id: 'home.views.tasks.filters.label.clearFilters',
		defaultMessage: 'Clear',
		description: "The text for the clear filters button who's action is to clear filters.",
	},
});

interface FilterFormProps {
	selectedTableView: TaskView;
	options?: {
		pages: TaskPages;
		spaces: TaskSpaces;
	};
	filterDispatch: Dispatch<any>;
	filterState: FiltersState;
}

export const FiltersForm = memo(
	({
		selectedTableView,
		filterDispatch,
		options = { pages: {}, spaces: {} },
		filterState,
	}: FilterFormProps) => {
		const { formatMessage } = useIntl();
		const [node, setNode] = useState<Element | null>(null);

		// Use this for now to override styles while we wait for changes in design system.
		// TODO: replace this when updated.
		useEffect(() => {
			const node = document.querySelector('[id^="ds--dropdown--"]');
			if (node) {
				setNode(node);
			}
		}, []);

		useEffect(() => {
			if (node) {
				(node as HTMLElement).style.overflow = 'visible';
				const menu = node.querySelector('div[role="menu"]');

				if (menu) {
					(menu as HTMLElement).style.overflow = 'visible';
				}
			}
		}, [node]);

		if (fg('confluence_frontend_home_compiled_migration')) {
			return (
				<div css={filtersContainerStyles} data-testid="filters-container">
					<InputContainerCompiled>
						<FilterHeaderCompiled>{formatMessage(i18n.filterLabel)}</FilterHeaderCompiled>
						<SelectContainerCompiled>
							<div css={clearContainerStyles}>
								<ClearButtonCompiled onClick={() => filterDispatch({ type: FilterKeys.DEFAULT })}>
									{formatMessage(i18n.clearFilters)}
								</ClearButtonCompiled>
							</div>
						</SelectContainerCompiled>
					</InputContainerCompiled>
					<SelectDueDate filterDispatch={filterDispatch} filterState={filterState} />
					<SelectCompletion filterState={filterState} filterDispatch={filterDispatch} />
					{selectedTableView === TaskView.Assigned ? (
						<SelectCreator filterState={filterState} filterDispatch={filterDispatch} />
					) : (
						<SelectAssignee filterState={filterState} filterDispatch={filterDispatch} />
					)}
					<SelectPage
						options={options.pages}
						filterDispatch={filterDispatch}
						filterState={filterState}
					/>
					<SelectSpace
						options={options.spaces}
						filterDispatch={filterDispatch}
						filterState={filterState}
					/>
				</div>
			);
		}

		return (
			<FiltersContainer data-testid="filters-container">
				<InputContainer>
					<FilterHeader>{formatMessage(i18n.filterLabel)}</FilterHeader>
					<SelectContainer>
						<ClearContainer>
							<ClearButton
								appearance="subtle-link"
								onClick={() => filterDispatch({ type: FilterKeys.DEFAULT })}
							>
								{formatMessage(i18n.clearFilters)}
							</ClearButton>
						</ClearContainer>
					</SelectContainer>
				</InputContainer>
				<SelectDueDate filterDispatch={filterDispatch} filterState={filterState} />
				<SelectCompletion filterState={filterState} filterDispatch={filterDispatch} />
				{selectedTableView === TaskView.Assigned ? (
					<SelectCreator filterState={filterState} filterDispatch={filterDispatch} />
				) : (
					<SelectAssignee filterState={filterState} filterDispatch={filterDispatch} />
				)}
				<SelectPage
					options={options.pages}
					filterDispatch={filterDispatch}
					filterState={filterState}
				/>
				<SelectSpace
					options={options.spaces}
					filterDispatch={filterDispatch}
					filterState={filterState}
				/>
			</FiltersContainer>
		);
	},
);
